import NavbarV2 from '../../components/navbarV2/NavbarV2';
import './ReportsV2.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReportsList from '../../components/ReportsList/ReportsList';
import PrintHero from '../../components/PrintHero/PrintHero';
import ListEmptyStateV2, { ListPageLoadingV2 } from '../../components/listEmptyStateV2/ListEmptyStateV2';
import { FaBoxOpen, FaPrint, FaSearch, FaTable } from 'react-icons/fa';
import { appendInclusiveDateRange } from '../../utils/inclusiveDateRange';

const formatDate = (date) => date.toISOString().split('T')[0];

const ReportsV2 = () => {
    const [list, setList] = useState([]);
    const [filter, setFilter] = useState('invoice');
    const [startDate, setStartDate] = useState(formatDate(new Date()));
    const [endDate, setEndDate] = useState(formatDate(new Date()));
    const [printing, setPrinting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetchDone, setFetchDone] = useState(false);
    const navigate = useNavigate();
    const isAdmin = localStorage.getItem('admin') === 'true';

    useEffect(() => {
        const onBeforePrint = () => setPrinting(true);
        const onAfterPrint = () => setPrinting(false);
        window.addEventListener('beforeprint', onBeforePrint);
        window.addEventListener('afterprint', onAfterPrint);
        return () => {
            window.removeEventListener('beforeprint', onBeforePrint);
            window.removeEventListener('afterprint', onAfterPrint);
        };
    }, []);

    const getList = useCallback(() => {
        setLoading(true);
        setList([]);

        let url = `${process.env.REACT_APP_API}/api/getReport`;

        const params = new URLSearchParams();

        if (filter.length > 0) params.append('filter', filter);
        appendInclusiveDateRange(params, startDate, endDate);

        url += `?${params.toString()}`;

        fetch(url)
            .then((response) => {
                if (response.status === 200) {
                    if (localStorage.getItem('account') !== null) {
                        response.json().then((data) => setList(data));
                    } else {
                        navigate('/');
                    }
                }
            })
            .catch((error) => {
                console.error('Error fetching the report:', error);
            })
            .finally(() => {
                setLoading(false);
                setFetchDone(true);
            });
    }, [filter, startDate, endDate, navigate]);

    const getListRef = useRef(getList);
    getListRef.current = getList;

    useEffect(() => {
        // Initial load on mount; Search runs getList() with current fields.
        getListRef.current();
    }, []);

    const print = () => {
        setPrinting(true);
        setTimeout(() => {
            window.print();
        }, 1000);
    };

    const filterLabel = filter === 'credit' ? 'credit notes' : 'invoices';

    return (
        <div className="reportsv2">
            {printing ? <PrintHero /> : <NavbarV2 />}

            <header className="reportsv2-header">
                <h1 className="reportsv2-title">Reports</h1>
                {isAdmin && !printing && (
                    <p className="reportsv2-lede">
                        Run totals for invoices or credit notes by date range. Use Print for a clean summary without navigation.
                    </p>
                )}
            </header>

            {isAdmin && !printing && (
                <div className="reportsv2-filters">
                    <div className="reportsv2-field">
                        <label htmlFor="reportsv2-type">Report type</label>
                        <select id="reportsv2-type" value={filter} onChange={(e) => setFilter(e.target.value)}>
                            <option value="invoice">Invoices</option>
                            <option value="credit">Credit notes</option>
                        </select>
                    </div>
                    <div className="reportsv2-field">
                        <label htmlFor="reportsv2-start">Start date</label>
                        <input
                            id="reportsv2-start"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className="reportsv2-field">
                        <label htmlFor="reportsv2-end">End date</label>
                        <input id="reportsv2-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                    <div className="reportsv2-actions">
                        <button type="button" className="reportsv2-btn reportsv2-btn--primary" onClick={() => getList()} disabled={loading}>
                            <FaSearch size={12} aria-hidden />
                            Search
                        </button>
                        <button type="button" className="reportsv2-btn reportsv2-btn--outline" onClick={() => print()} disabled={loading}>
                            <FaPrint size={12} aria-hidden />
                            Print
                        </button>
                        <Link to="/ikeaorders" className="reportsv2-btn reportsv2-btn--secondary reportsv2-btn--link">
                            <FaBoxOpen size={14} aria-hidden />
                            SKU orders
                        </Link>
                    </div>
                </div>
            )}

            {loading && <ListPageLoadingV2 />}

            {!loading && fetchDone && list.length === 0 && (
                <ListEmptyStateV2
                    icon={FaTable}
                    title="No rows for this range"
                    description={
                        isAdmin
                            ? `No ${filterLabel} matched the selected dates. Adjust the range or report type and search again.`
                            : `No ${filterLabel} are available for your account for the current report request.`
                    }
                />
            )}

            {!loading && list.length > 0 && <ReportsList data={list} />}
        </div>
    );
};

export default ReportsV2;
